import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpenseReportsBox, {
  ExpenseReportsAnalyticsEventProperties,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import LayoutShell from 'src/components/LayoutShell/LayoutShell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign } from 'src/components/Subnav/Subnav'
import {
  UserDropdownInfiniteScrollProps,
  userIdOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useManagerReviewRulesEnabled } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import { expenseReportsQueries } from 'src/queries/expenseReports/expenseReports.queries'
import {
  isOutOfPocketExpensesVisible,
  orgOutOfPocketExpensesQueries,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { useQueryPeopleManagerDirectReports } from 'src/queries/organizations/peopleManagers.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const ManagerApproverExpenseReports: React.FC = () => {
  const translate = useTranslate()

  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const outOfPocketExpensesQuery = useQuery(
    orgOutOfPocketExpensesQueries.enabled(organization?.id),
  )
  const gettingOutOfPocketExpensesEnabled = outOfPocketExpensesQuery.isLoading
  const outOfPocketExpensesEnabled = isOutOfPocketExpensesVisible(
    organization,
    outOfPocketExpensesQuery.data,
  )
  const submitterUsersQuery = useQuery(expenseReportsQueries.submitterUsers())
  const allMemberIdOptions = useMemo(
    () =>
      (submitterUsersQuery.data ?? []).map((user) =>
        userIdOption(new UserListModel(user)),
      ),
    [submitterUsersQuery.data],
  )
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()
  const [selectedFilters, setSelectedFilters] = useState<
    ExpenseReportsAnalyticsEventProperties | undefined
  >(undefined)

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: () => {
      void submitterUsersQuery.refetch()
    },
    loading: submitterUsersQuery.isFetching,
    complete: !submitterUsersQuery.isPending,
  }

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track(`manager expense reports filter applied`, {
        report_status: selectedFilters.report_status,
        report_submitted_by: selectedFilters.report_submitted_by,
      })
    }
  }, [selectedFilters])

  const user = useCurrentUser()
  const managerReviewRulesEnabled = useManagerReviewRulesEnabled()
  const { data: directReports } = useQueryPeopleManagerDirectReports({
    organizationId: user?.organizationId,
    userId: user?.id,
  })
  const showManagerReviewContent =
    directReports !== undefined &&
    directReports.length > 0 &&
    managerReviewRulesEnabled

  const content = gettingOutOfPocketExpensesEnabled ? (
    <LoadingBackgroundPage />
  ) : outOfPocketExpensesEnabled && showManagerReviewContent ? (
    <>
      <Helmet>
        <title>{`Reimbursements | ${appName}`}</title>
        <meta
          name="description"
          content={`Reimbursement Reports page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <ExpenseReportsBox
          userOptions={allMemberIdOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
          setSelectedFilters={setSelectedFilters}
          context={'manager'}
        />
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPageContent />
  )

  return (
    <LayoutShell
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={[]}
      content={content}
      title={translate('Reimbursements')}
      /* @ts-ignore next-line */
      buttons={[]}
    />
  )
}

export default ManagerApproverExpenseReports
